import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { toast } from "react-toastify";
import { getBackendBaseUrl } from "../../../utils/apiUrl";
import { FaShieldAlt, FaPlus, FaTrash, FaTimes, FaStar, FaEdit, FaCheckCircle, FaInfoCircle } from "react-icons/fa";

const INITIAL_FORM = {
  name: "",
  shortName: "",
  category: "Mechanical Protection",
  description: "",
  term: "7 yr / 100,000 mi",
  price: 3100,
  isTaxable: false,
  isBasePlan: false,
  selectScript: "",
  unselectScript: "",
  reselectScript: "",
};

const D2FAndI = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(INITIAL_FORM);

  const user = JSON.parse(localStorage.getItem("user") || "{}");

  const fetchServices = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const res = await fetch(`${getBackendBaseUrl()}/d2-fandi`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        setServices(data.data || []);
      }
    } catch (err) {
      console.error("Failed to fetch F&I services", err);
      toast.error("Failed to load F&I services");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchServices();
  }, []);

  const handleOpenAdd = () => {
    setEditingId(null);
    setFormData(INITIAL_FORM);
    setShowModal(true);
  };

  const handleOpenEdit = (service) => {
    setEditingId(service._id);
    setFormData({
      name: service.name || "",
      shortName: service.shortName || "",
      category: service.category || "Mechanical Protection",
      description: service.description || "",
      term: service.term || "7 yr / 100,000 mi",
      price: service.price || 0,
      isTaxable: !!service.isTaxable,
      isBasePlan: !!service.isBasePlan,
      selectScript: service.selectScript || "",
      unselectScript: service.unselectScript || "",
      reselectScript: service.reselectScript || "",
    });
    setShowModal(true);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleMakeBasePlan = async (service) => {
    try {
      const token = localStorage.getItem("authToken");
      const res = await fetch(`${getBackendBaseUrl()}/d2-fandi/${service._id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ isBasePlan: true }),
      });
      const data = await res.json();
      if (data.success) {
        toast.success(`"${service.name}" is now the Base Protection Plan!`);
        fetchServices();
      } else {
        toast.error(data.message || "Failed to set base plan");
      }
    } catch (err) {
      toast.error("Failed to set base plan");
    }
  };

  const handleSaveService = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem("authToken");
      const url = editingId
        ? `${getBackendBaseUrl()}/d2-fandi/${editingId}`
        : `${getBackendBaseUrl()}/d2-fandi`;
      const method = editingId ? "PUT" : "POST";

      const res = await fetch(url, {
        method: method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      });
      const data = await res.json();
      if (data.success) {
        toast.success(editingId ? "F&I Service updated successfully!" : "F&I Service added successfully!");
        setShowModal(false);
        setEditingId(null);
        setFormData(INITIAL_FORM);
        fetchServices();
      } else {
        toast.error(data.message || "Failed to save F&I Service");
      }
    } catch (err) {
      console.error("Error saving service", err);
      toast.error("Failed to save F&I Service");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this service?")) return;
    try {
      const token = localStorage.getItem("authToken");
      const res = await fetch(`${getBackendBaseUrl()}/d2-fandi/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        toast.success("Service deleted");
        fetchServices();
      }
    } catch (err) {
      toast.error("Failed to delete service");
    }
  };

  return (
    <div className="p-8 h-full w-full bg-slate-50/50 flex flex-col overflow-y-auto animate-fade-in-up">
      {/* Title Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-extrabold text-slate-800 tracking-tight flex items-center gap-3">
            <span className="p-2.5 bg-emerald-50 text-emerald-600 rounded-2xl shadow-sm border border-emerald-100">
              <FaShieldAlt size={24} />
            </span>
            F&I Protection Plans
          </h1>
          <p className="text-slate-500 text-xs mt-1 leading-relaxed">
            Configure financial products, Base Plan designation, and interactive script dialogs for customer selection/unselection.
          </p>
        </div>
        {user?.role !== "Salesperson" && (
          <button
            onClick={handleOpenAdd}
            className="bg-gradient-to-r from-indigo-600 to-blue-600 text-white px-5 py-2.5 rounded-xl font-semibold hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0 shadow-md transition-all duration-200 flex items-center gap-2 text-sm cursor-pointer"
          >
            <FaPlus size={12} /> Add F&I Service
          </button>
        )}
      </div>

      {/* Services Table Container */}
      <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200/80 text-slate-500 text-xs font-bold uppercase tracking-wider">
                <th className="px-6 py-4">Plan Role</th>
                <th className="px-6 py-4">Service Name</th>
                <th className="px-6 py-4">Category</th>
                <th className="px-6 py-4">Coverage Term</th>
                <th className="px-6 py-4">Price ($)</th>
                <th className="px-6 py-4">Taxable</th>
                {user?.role !== "Salesperson" && <th className="px-6 py-4 text-right">Actions</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr>
                  <td colSpan={user?.role === "Salesperson" ? "6" : "7"} className="px-6 py-10 text-center text-slate-400 text-sm">
                    <div className="flex flex-col items-center justify-center gap-2">
                      <span className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></span>
                      Loading F&I services...
                    </div>
                  </td>
                </tr>
              ) : services.length === 0 ? (
                <tr>
                  <td colSpan={user?.role === "Salesperson" ? "6" : "7"} className="px-6 py-12 text-center text-slate-400 text-sm italic">
                    No F&I services found. Click "Add F&I Service" to add protection products.
                  </td>
                </tr>
              ) : (
                services.map((service) => (
                  <tr key={service._id} className={`hover:bg-slate-50/50 transition-colors ${service.isBasePlan ? 'bg-amber-50/20' : ''}`}>
                    <td className="px-6 py-4">
                      {service.isBasePlan ? (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-gradient-to-r from-amber-500 to-yellow-500 text-slate-950 font-black text-xs rounded-lg shadow-sm">
                          <FaStar size={11} /> BASE PLAN
                        </span>
                      ) : (
                        <button
                          onClick={() => handleMakeBasePlan(service)}
                          title="Click to set as single Base Plan"
                          className="text-[11px] font-semibold text-slate-500 hover:text-amber-600 bg-slate-100 hover:bg-amber-50 border border-slate-200 hover:border-amber-300 px-2.5 py-1 rounded-lg transition-all cursor-pointer"
                        >
                          Set as Base Plan
                        </button>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="font-bold text-slate-800 text-sm flex items-center gap-2">
                        {service.name}
                      </div>
                      {service.description && <div className="text-xs text-slate-400 mt-0.5 line-clamp-1">{service.description}</div>}
                    </td>
                    <td className="px-6 py-4 text-xs font-semibold text-slate-600">
                      <span className="px-2.5 py-1 bg-emerald-50 text-emerald-700 border border-emerald-100 rounded-lg">
                        {service.category || "Protection Plan"}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-xs text-slate-600 font-medium">
                      {service.term || "72 months"}
                    </td>
                    <td className="px-6 py-4 text-sm font-extrabold text-emerald-600">
                      ${Number(service.price).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      {service.isTaxable ? (
                        <span className="px-2.5 py-1 bg-amber-50 text-amber-700 border border-amber-100/50 rounded-lg text-xs font-semibold">Yes</span>
                      ) : (
                        <span className="px-2.5 py-1 bg-slate-100 text-slate-600 rounded-lg text-xs font-semibold">No</span>
                      )}
                    </td>
                    {user?.role !== "Salesperson" && (
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleOpenEdit(service)}
                            className="text-indigo-600 hover:text-indigo-800 font-semibold text-xs tracking-wide bg-indigo-50 hover:bg-indigo-100 px-3 py-1.5 rounded-lg transition-colors border border-indigo-200/40 flex items-center gap-1 cursor-pointer"
                          >
                            <FaEdit size={11} /> Edit
                          </button>
                          <button
                            onClick={() => handleDelete(service._id)}
                            className="text-red-500 hover:text-red-700 font-semibold text-xs tracking-wide bg-red-50 hover:bg-red-100 px-3 py-1.5 rounded-lg transition-colors border border-red-200/30 flex items-center gap-1 cursor-pointer"
                          >
                            <FaTrash size={10} /> Delete
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* True Fullscreen Modal using ReactDOM.createPortal */}
      {showModal && ReactDOM.createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/70 backdrop-blur-sm p-4 overflow-y-auto animate-fade-in-up">
          <div className="bg-white rounded-3xl border border-slate-200 shadow-2xl p-8 w-full max-w-2xl max-h-[92vh] overflow-y-auto relative my-auto">
            <button 
              onClick={() => setShowModal(false)}
              className="absolute top-6 right-6 text-slate-400 hover:text-slate-600 p-2 hover:bg-slate-100 rounded-full transition-colors cursor-pointer"
            >
              <FaTimes size={16} />
            </button>
            
            <h2 className="text-2xl font-bold text-slate-800 tracking-tight mb-1">
              {editingId ? "Edit F&I Service" : "Add F&I Service"}
            </h2>
            <p className="text-slate-400 text-xs mb-5 border-b pb-3">
              Configure plan properties, base plan setting, and dynamic script dialogs for customer interactions.
            </p>
            
            <form onSubmit={handleSaveService} className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-600 uppercase mb-1">Service Name *</label>
                <input
                  required
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="w-full border border-slate-200 rounded-xl p-3 text-sm bg-slate-50/50 focus:bg-white outline-none"
                  placeholder="e.g. Vehicle Service Contract (VSC)"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-600 uppercase mb-1">Category</label>
                  <select name="category" value={formData.category} onChange={handleInputChange} className="w-full border border-slate-200 rounded-xl p-3 text-sm bg-slate-50/50 focus:bg-white outline-none">
                    <option value="Mechanical Protection">Mechanical Protection</option>
                    <option value="Scheduled Care">Scheduled Care</option>
                    <option value="Cosmetic & Road Hazard">Cosmetic & Road Hazard</option>
                    <option value="Loan Protection">Loan Protection</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-600 uppercase mb-1">Coverage Term</label>
                  <input
                    type="text"
                    name="term"
                    value={formData.term}
                    onChange={handleInputChange}
                    className="w-full border border-slate-200 rounded-xl p-3 text-sm bg-slate-50/50 focus:bg-white outline-none"
                    placeholder="7 yr / 100,000 mi"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-600 uppercase mb-1">Description</label>
                <textarea
                  rows="2"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  className="w-full border border-slate-200 rounded-xl p-3 text-sm bg-slate-50/50 focus:bg-white outline-none resize-none"
                  placeholder="Explain coverages, terms, or specifications..."
                ></textarea>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-600 uppercase mb-1">Retail Price ($) *</label>
                  <input
                    required
                    type="number"
                    name="price"
                    value={formData.price}
                    onChange={handleInputChange}
                    className="w-full border border-slate-200 rounded-xl p-3 text-sm bg-slate-50/50 focus:bg-white outline-none"
                    placeholder="3100"
                  />
                </div>

                <div className="flex flex-col justify-center space-y-2 pt-2 sm:pt-4">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="isTaxable"
                      name="isTaxable"
                      checked={formData.isTaxable}
                      onChange={handleInputChange}
                      className="w-4 h-4 rounded text-indigo-600 focus:ring-indigo-500 border-slate-300 cursor-pointer"
                    />
                    <label htmlFor="isTaxable" className="text-xs text-slate-700 font-semibold cursor-pointer">
                      Taxable product under sales tax
                    </label>
                  </div>
                </div>
              </div>

              {/* Base Plan Checkbox Banner */}
              <div className={`p-4 rounded-2xl border transition-all ${formData.isBasePlan ? 'bg-amber-50 border-amber-200' : 'bg-slate-50 border-slate-200'}`}>
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="isBasePlan"
                    name="isBasePlan"
                    checked={formData.isBasePlan}
                    onChange={handleInputChange}
                    className="w-5 h-5 rounded text-amber-600 focus:ring-amber-500 border-slate-300 mt-0.5 cursor-pointer"
                  />
                  <div>
                    <label htmlFor="isBasePlan" className="text-sm text-slate-800 font-extrabold cursor-pointer flex items-center gap-1.5">
                      <FaStar className="text-amber-500" /> Set as Dealership Base Plan
                    </label>
                    <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">
                      Only <strong>one plan</strong> can be the Base Plan. If the customer unselects this Base Plan in the kiosk, <strong>all other protection plans will automatically be unselected</strong>.
                    </p>
                  </div>
                </div>
              </div>

              {/* Dynamic Script Placeholder Helper Info */}
              <div className="bg-indigo-50/60 border border-indigo-100 rounded-2xl p-3.5 space-y-1 text-xs">
                <div className="flex items-center gap-1.5 text-indigo-800 font-bold">
                  <FaInfoCircle size={13} />
                  <span>Available Dynamic Placeholders for Dialogs:</span>
                </div>
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {["{customerName}", "{planName}", "{price}", "{monthlyPrice}", "{totalProtectionPrice}", "{vehicleName}"].map(token => (
                    <span key={token} className="px-2 py-0.5 bg-white border border-indigo-200 text-indigo-700 font-mono text-[11px] rounded-md shadow-2xs font-semibold">
                      {token}
                    </span>
                  ))}
                </div>
              </div>

              {/* Dialog Scripts Section */}
              <div className="space-y-3 border-t border-slate-100 pt-3">
                <h3 className="text-xs font-black uppercase tracking-wider text-slate-700">
                  {formData.isBasePlan ? "Base Plan Script Dialog" : "Add-on Script Dialogs"}
                </h3>

                {formData.isBasePlan ? (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-bold text-rose-600 uppercase mb-1">
                        1. Base Plan Unselection Warning Script *
                      </label>
                      <p className="text-[11px] text-slate-400 mb-1">
                        Shown to customer in an inline warning banner on the kiosk when they unselect this base plan.
                      </p>
                      <textarea
                        rows="2"
                        name="unselectScript"
                        value={formData.unselectScript}
                        onChange={handleInputChange}
                        className="w-full border border-rose-200 bg-rose-50/30 rounded-xl p-3 text-xs text-slate-800 focus:bg-white outline-none"
                        placeholder="⚠️ Declining {planName} drops all protection plans (${totalProtectionPrice} total value). You will be 100% liable for out-of-pocket repairs on your {vehicleName}."
                      ></textarea>
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-cyan-600 uppercase mb-1">
                        2. Base Plan Reselection Script (Restore All Plans)
                      </label>
                      <textarea
                        rows="2"
                        name="reselectScript"
                        value={formData.reselectScript}
                        onChange={handleInputChange}
                        className="w-full border border-cyan-200 bg-cyan-50/20 rounded-xl p-3 text-xs text-slate-800 focus:bg-white outline-none"
                        placeholder="Welcome back, {customerName}! Base plan and all previous protection coverages have been fully restored for your {vehicleName}."
                      ></textarea>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-bold text-emerald-600 uppercase mb-1">
                        1. Select Script (First-Time Selection)
                      </label>
                      <textarea
                        rows="2"
                        name="selectScript"
                        value={formData.selectScript}
                        onChange={handleInputChange}
                        className="w-full border border-emerald-200 bg-emerald-50/20 rounded-xl p-3 text-xs text-slate-800 focus:bg-white outline-none"
                        placeholder="Great choice, {customerName}! Adding {planName} for {monthlyPrice}/mo (${price} total) ensures you are protected from unexpected repairs."
                      ></textarea>
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-rose-600 uppercase mb-1">
                        2. Unselect Script (Decline Warning)
                      </label>
                      <textarea
                        rows="2"
                        name="unselectScript"
                        value={formData.unselectScript}
                        onChange={handleInputChange}
                        className="w-full border border-rose-200 bg-rose-50/20 rounded-xl p-3 text-xs text-slate-800 focus:bg-white outline-none"
                        placeholder="{customerName}, declining {planName} exposes you to full retail out-of-pocket costs. Are you sure you want to remove this?"
                      ></textarea>
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-cyan-600 uppercase mb-1">
                        3. Reselect Script (Re-adding after previously unselecting)
                      </label>
                      <textarea
                        rows="2"
                        name="reselectScript"
                        value={formData.reselectScript}
                        onChange={handleInputChange}
                        className="w-full border border-cyan-200 bg-cyan-50/20 rounded-xl p-3 text-xs text-slate-800 focus:bg-white outline-none"
                        placeholder="Welcome back, {customerName}! We have re-added {planName} (+{monthlyPrice}/mo) to your plan."
                      ></textarea>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-3 mt-4 border-t pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-5 py-2.5 border border-slate-200 rounded-xl text-slate-600 hover:bg-slate-50 font-semibold text-sm transition-all cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2.5 bg-gradient-to-r from-indigo-600 to-blue-600 rounded-xl text-white font-semibold hover:shadow-lg shadow-md transition-all text-sm cursor-pointer flex items-center gap-2"
                >
                  <FaCheckCircle size={14} /> {editingId ? "Update F&I Service" : "Save F&I Service"}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default D2FAndI;

